import { z } from "zod";
import {
    MatchingStrategyEnum,
    UrgencyEnum,
    DraftLeadExpertSchema,
    DraftLeadPackagedServiceSchema,
    DraftLeadProjectValueRangeSchema,
    DraftLeadAdminSchema,
} from "./common";

// ==================
// Base Draft Lead Schema (shared fields)
// ==================

const DraftLeadBaseSchema = z.object({
    token: z.string(),
    /** Email can be empty string during progressive flow */
    email: z.string(),
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    project_title: z.string().nullable(),
    project_description: z.string().nullable(),
    store_url: z.string().nullable(),
    brand_name: z.string().nullable(),
    urgency: UrgencyEnum.nullable(),
    matching_strategy: MatchingStrategyEnum.nullable(),
    is_complete: z.boolean(),
    is_submitted: z.boolean(),
    /** List of missing required field names - accept any string from API */
    missing_fields: z.array(z.string()),
    selected_expert: DraftLeadExpertSchema.nullable(),
    selected_packaged_service: DraftLeadPackagedServiceSchema.nullable(),
    project_value_range: DraftLeadProjectValueRangeSchema.nullable(),
    created_at: z.string(),
    updated_at: z.string(),
});

// ==================
// Public Response Schemas
// ==================

/** Draft lead response for public endpoints (token-based access) */
export const DraftLeadPublicResponseSchema = DraftLeadBaseSchema;

export type DraftLeadPublicResponse = z.infer<typeof DraftLeadPublicResponseSchema>;

/** Response for check-auth endpoint */
export const CheckAuthResponseSchema = z.object({
    needs_auth: z.boolean(),
});

export type CheckAuthResponse = z.infer<typeof CheckAuthResponseSchema>;

/** Response after submitting a draft lead */
export const SubmitDraftLeadResponseSchema = z.object({
    lead_id: z.number().int().positive(),
    is_new_account: z.boolean(),
});

export type SubmitDraftLeadResponse = z.infer<typeof SubmitDraftLeadResponseSchema>;

// ==================
// Admin Response Schemas
// ==================

/** Draft lead response for admin endpoints (includes id, admin info, submission details) */
export const DraftLeadAdminResponseSchema = DraftLeadBaseSchema.extend({
    id: z.number().int().positive(),
    timezone: z.string().nullable().optional(),
    /** Admin who created the draft (null if created via public endpoint) */
    created_by_admin: DraftLeadAdminSchema.nullable(),
    /** Timestamp when the draft was submitted */
    submitted_at: z.string().nullable(),
    /** ID of the lead created after submission */
    submitted_lead_id: z.number().int().positive().nullable(),
});

export type DraftLeadAdminResponse = z.infer<typeof DraftLeadAdminResponseSchema>;

/** Response after admin submits a draft lead */
export const AdminSubmitDraftLeadResponseSchema = z.object({
    lead_id: z.number().int().positive(),
    is_new_account: z.boolean(),
});

export type AdminSubmitDraftLeadResponse = z.infer<typeof AdminSubmitDraftLeadResponseSchema>;
